import React, { FC, SVGProps, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styles from './Cart.module.scss';
import { fetchOrderForm, submitOrder } from '../../../api/orders';
import { useCart } from '../../../utils/hook/useCart';
import {
    CustomerInfoEntry,
    OrderConfirmation,
    OrderFormField,
    PaymentPreference,
} from '../../types/CartType';
import { ReactComponent as ArrowBack } from '../../../assets/icons/carret.left.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { useUiString, useUiTemplate } from '../../../utils/hook/useUiString';
import { UiKey } from '../../../utils/localization/uiStrings';
import { useFulfillmentOptions } from '../../../utils/hook/useFulfillment';
import { SelectInput } from './SelectInput';
import { CardIcon, LockIcon, RegisterIcon, RoomIcon } from './icons';

// The fields come from the restaurant, not from us — each one configures
// its own checkout questions in the TLG dashboard.

interface IProps {
    menuId: number;
    onBack: () => void;
    onClose: () => void;
    /** The confirmation carries the payment block when the restaurant takes
     *  card payments — the drawer decides whether to charge or finish. */
    onSuccess: (confirmation: OrderConfirmation) => void;
}

interface PaymentChoice {
    value: PaymentPreference;
    label: UiKey;
    Icon: FC<SVGProps<SVGSVGElement>>;
}

const SKELETON_ROWS = 3;

// One id per checkout attempt. Reused across retries so a failed-then-
// retried submit cannot land as two orders.
function newOrderId(): string {
    const webCrypto = globalThis.crypto;
    if (webCrypto && typeof webCrypto.randomUUID === 'function') return webCrypto.randomUUID();
    return `web-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function readError(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const message = (error.response?.data as { message?: string } | undefined)?.message;
        if (message) return message;
    }
    return error instanceof Error ? error.message : fallback;
}

// The form step only collects the guest's details; how they pay is asked
// on its own screen, between here and the card/wallet step.
type Step = 'form' | 'payment';

export const CheckoutForm: FC<IProps> = ({ menuId, onBack, onClose, onSuccess }) => {
    const t = useUiString();
    const tpl = useUiTemplate();
    const { items, clear } = useCart();
    const { cardOffered, venue } = useFulfillmentOptions();
    const [step, setStep] = useState<Step>('form');
    const [preference, setPreference] = useState<PaymentPreference>('now');
    const [fields, setFields] = useState<OrderFormField[]>([]);
    const [values, setValues] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const orderId = useRef(newOrderId());

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const form = await fetchOrderForm(menuId);
                if (cancelled) return;
                setFields(form.fields);
            } catch (err) {
                if (!cancelled) setError(readError(err, t('somethingWentWrong')));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [menuId, t]);

    // A venue without Square never asks, so its guests always settle at
    // the counter — the same place a guest who declined the card ends up.
    const settlesAtVenue = !cardOffered || preference === 'venue';
    const choices: PaymentChoice[] = [
        { value: 'now', label: 'payNow', Icon: CardIcon },
        venue === 'room'
            ? { value: 'venue', label: 'chargeRoom', Icon: RoomIcon }
            : { value: 'venue', label: 'payAtRegister', Icon: RegisterIcon },
    ];

    const setValue = (key: string, value: string) => {
        setError(null);
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const firstMissing = () =>
        fields.find((field) => field.required && !(values[field.key] ?? '').trim());

    const send = async () => {
        const missing = firstMissing();
        if (missing) {
            setError(tpl('fieldRequired', { field: missing.label }));
            return;
        }

        const customerInfo: CustomerInfoEntry[] = fields
            .map((field) => ({
                key:   field.key,
                label: field.label,
                value: (values[field.key] ?? '').trim(),
            }))
            .filter((entry) => entry.value !== '');

        setSubmitting(true);
        setError(null);
        try {
            const confirmation = await submitOrder({
                menu_id:       menuId,
                order_id:      orderId.current,
                customer_info: customerInfo,
                items:         items.map((item) => ({ dish_id: item.dishId, quantity: item.quantity })),
                payment_preference: settlesAtVenue ? 'venue' : 'now',
            });
            // The basket is only emptied once there is nothing left to pay.
            // Clearing it before a card is charged would strand a guest whose
            // payment fails with no way back.
            if (!confirmation.payment) clear();
            onSuccess(confirmation);
        } catch (err) {
            setError(readError(err, t('somethingWentWrong')));
        } finally {
            setSubmitting(false);
        }
    };

    // "Place Order" on the form step checks the details and hands over to
    // the payment-choice screen. Only then, from that screen, is the order
    // actually sent. A venue without Square skips the choice — its guests
    // always settle at the counter.
    const placeOrder = () => {
        const missing = firstMissing();
        if (missing) {
            setError(tpl('fieldRequired', { field: missing.label }));
            return;
        }
        setError(null);
        if (cardOffered) {
            setStep('payment');
            return;
        }
        void send();
    };

    const headerTitle = step === 'payment' ? t('choosePayment') : t('checkout');
    const goBack = () => (step === 'payment' ? setStep('form') : onBack());

    return (
        <>
            <div className={styles.sheetHeader}>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={goBack}
                    aria-label={t('backToCart')}
                >
                    <ArrowBack className={styles.iconSvg} />
                </button>
                <h4 className={styles.sheetTitle}>{headerTitle}</h4>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={onClose}
                    aria-label={t('closeCart')}
                >
                    <CloseIcon className={styles.iconSvg} />
                </button>
            </div>

            {loading ? (
                <div className={styles.scroll} aria-busy="true" aria-label={t('loading')}>
                    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                        <div key={index} className={styles.skeleton} />
                    ))}
                </div>
            ) : step === 'payment' ? (
                <>
                    <div className={styles.scroll}>
                        <div className={styles.choiceCards} role="radiogroup">
                            {choices.map(({ value, label, Icon }) => {
                                const active = preference === value;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        className={
                                            active
                                                ? `${styles.choiceCard} ${styles.choiceCardActive}`
                                                : styles.choiceCard
                                        }
                                        onClick={() => setPreference(value)}
                                    >
                                        <span className={styles.choiceIcon}>
                                            <Icon />
                                        </span>
                                        <span className={styles.choiceText}>{t(label)}</span>
                                        <span
                                            className={
                                                active ? `${styles.radio} ${styles.radioActive}` : styles.radio
                                            }
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.sheetFooter}>
                        {error && <p className={styles.error}>{error}</p>}
                        <p className={styles.secureNote}>
                            <LockIcon />
                            {t('paymentDataDisclaimer')}
                        </p>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={() => void send()}
                            disabled={submitting || items.length === 0}
                        >
                            {submitting ? t('sending') : t('placeOrder')}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.scroll}>
                        {fields.map((field) => (
                            <label key={field.key} className={styles.field}>
                                <span className={styles.label}>
                                    {field.label}
                                    {field.required && <span className={styles.required}>*</span>}
                                </span>
                                {field.type === 'textarea' ? (
                                    <textarea
                                        className={`${styles.input} ${styles.textarea}`}
                                        value={values[field.key] ?? ''}
                                        onChange={(e) => setValue(field.key, e.target.value)}
                                    />
                                ) : field.type === 'select' ? (
                                    <SelectInput
                                        value={values[field.key] ?? ''}
                                        options={field.options ?? []}
                                        onChange={(next) => setValue(field.key, next)}
                                    />
                                ) : (
                                    <input
                                        className={
                                            field.type === 'number'
                                                ? `${styles.input} ${styles.numberInput}`
                                                : styles.input
                                        }
                                        type={field.type === 'number' ? 'number' : 'text'}
                                        inputMode={field.type === 'number' ? 'numeric' : undefined}
                                        value={values[field.key] ?? ''}
                                        onChange={(e) => setValue(field.key, e.target.value)}
                                    />
                                )}
                            </label>
                        ))}
                    </div>

                    <div className={styles.sheetFooter}>
                        {error && <p className={styles.error}>{error}</p>}
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={placeOrder}
                            disabled={items.length === 0}
                        >
                            {t('placeOrder')}
                        </button>
                    </div>
                </>
            )}
        </>
    );
};
